import { Injectable, Injector, InjectionToken, Type, inject } from '@angular/core';
import { Overlay, OverlayRef, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

/** Inject this in dropdown components to read the data passed by open(). */
export const TOPBAR_POPOVER_DATA = new InjectionToken<Record<string, unknown>>('TopbarPopoverData');

/** Inject this to give dropdown components a close() function. */
export const TOPBAR_POPOVER_CLOSE = new InjectionToken<() => void>('TopbarPopoverClose');

@Injectable({ providedIn: 'root' })
export class TopbarPopoverService {
  private overlay = inject(Overlay);
  private parentInjector = inject(Injector);

  private currentRef: OverlayRef | null = null;
  private currentAnchor: HTMLElement | null = null;

  constructor() {
    // ROR-3486: a popover belongs to the page it was opened on. Left open
    // across a route change (e.g. Basket → Compare) it sat over the new
    // page's header and first product column. Root service, so this
    // subscription lives as long as the app — no teardown needed.
    inject(Router).events
      .pipe(filter((e) => e instanceof NavigationStart))
      .subscribe(() => this.close());
  }

  isOpen(): boolean { return this.currentRef != null; }

  open<T>(opts: { component: Type<T>; anchor: HTMLElement; data?: Record<string, unknown> }): void {
    if (this.currentRef && this.currentAnchor === opts.anchor) { this.close(); return; }
    this.close();

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(opts.anchor)
      .withPositions([
        { originX: 'end',   originY: 'bottom', overlayX: 'end',   overlayY: 'top',    offsetY: 6 },
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top',    offsetY: 6 },
        { originX: 'end',   originY: 'top',    overlayX: 'end',   overlayY: 'bottom', offsetY: -6 },
      ])
      .withFlexibleDimensions(true)
      .withPush(true);

    const config = new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'topbar-popover-panel',
    });

    const overlayRef = this.overlay.create(config);
    this.currentRef = overlayRef;
    this.currentAnchor = opts.anchor;

    overlayRef.backdropClick().subscribe(() => this.close());
    overlayRef.keydownEvents().subscribe((e) => { if (e.key === 'Escape') this.close(); });

    const popoverInjector = Injector.create({
      parent: this.parentInjector,
      providers: [
        { provide: TOPBAR_POPOVER_DATA, useValue: opts.data ?? {} },
        { provide: TOPBAR_POPOVER_CLOSE, useValue: () => this.close() },
      ],
    });

    const portal = new ComponentPortal(opts.component, null, popoverInjector);
    overlayRef.attach(portal);
  }

  close(): void {
    if (this.currentRef) {
      this.currentRef.dispose();
      this.currentRef = null;
      this.currentAnchor = null;
    }
  }
}
